import { PlaceSummary } from "@/types/place"
import { ChatServerEventData } from "@/types/streamed-messages"
import remove from "lodash/remove"

import { createFrameBatch } from "./frame-batch"
import { openExploreEventSource } from "./open-explore-event-source"
import {
  addRecentLocation,
  addStreamedPlaces,
  setIsStreaming,
  setLastStrippedQuery,
  setLoadingResults,
  setLocation,
  setPagination,
  setSuggestedTabOrder,
  stopPlaceStreaming,
} from "./storage/explore-slice"
import { AppDispatch } from "./storage/redux-store"

export class ExploreStreamListener {
  static activeListeners: ExploreStreamListener[] = []
  private eventSource: EventSource
  private streaming = false
  // the server sends one place per message; the store sees them per frame
  private places = createFrameBatch<PlaceSummary>((places) => {
    this.dispatch(addStreamedPlaces(places))
    this.dispatch(setLoadingResults(false))
  })

  static listenToStream(streamId: string, dispatch: AppDispatch) {
    if (!this.activeListeners.some((s) => s.streamId === streamId)) {
      const newListener = new ExploreStreamListener(streamId, dispatch)
      this.activeListeners.push(newListener)
      return newListener
    }
  }

  static closeActiveListeners() {
    this.activeListeners.forEach((listener) => listener.close())
  }

  private constructor(
    private streamId: string,
    private dispatch: AppDispatch
  ) {
    this.eventSource = this.openConnection()
  }

  private openConnection(): EventSource {
    const eventSource = openExploreEventSource(this.streamId)
    eventSource.onerror = (event) => this.onError(event)
    eventSource.onmessage = (event: MessageEvent) => this.onMessage(event)
    return eventSource
  }

  private async onMessage(event: MessageEvent) {
    try {
      const parsedPayload: ChatServerEventData = JSON.parse(event.data)
      const { status } = parsedPayload

      if (status === "in-progress") {
        if (!this.streaming) {
          this.streaming = true
          this.dispatch(setIsStreaming(true))
        }
        const chunks = parsedPayload.data
        chunks.forEach((chunk) => {
          if (chunk.type === "place" && chunk.place) {
            this.places.push(chunk.place)
          }
          if (chunk.type === "search_geo_changed" && chunk.geo) {
            this.dispatch(
              setLocation({
                location: chunk.geo,
                isUserSelected: false,
                isSearchSuggested: true,
              })
            )

            this.dispatch(setLastStrippedQuery(chunk.stripped_query))
            this.dispatch(addRecentLocation(chunk.geo))

            if (chunk.suggested_tab_order) {
              this.dispatch(setSuggestedTabOrder(chunk.suggested_tab_order))
            }
          }
          if (chunk.type === "pagination_info" && chunk.pagination_info) {
            this.dispatch(setPagination(chunk.pagination_info))
          }
        })
      } else if (status === "done") {
        this.places.flush()
        this.dispatch(stopPlaceStreaming())
        this.close()
      } else if (status === "error") {
        console.error(`Explore stream error: ${parsedPayload.data.message}`)
        this.places.flush()
        this.dispatch(setLoadingResults(false))
        this.close()
      }
    } catch (e) {
      console.error("Error processing explore stream message:", e)
      this.places.flush()
      this.dispatch(setLoadingResults(false))
      this.close()
    }
  }

  private async onError(event: Event) {
    console.error("Explore stream error:", event)
    this.places.flush()
    this.dispatch(setLoadingResults(false))
    this.close()
  }

  // Closing mid-stream (a new search replaced this one) drops what hasn't
  // been shown; the ended paths flush first.
  close() {
    if (this.eventSource.readyState !== EventSource.CLOSED) {
      this.eventSource.close()
    }
    this.places.cancel()
    this.dispatch(setIsStreaming(false))
    remove(
      ExploreStreamListener.activeListeners,
      (listener) => listener.streamId === this.streamId
    )
  }
}
